import { useCallback, useEffect, useRef } from 'react';

/**
 * The one message every leave-guard surface shows.
 *
 * OK = STAY, on purpose: the copy is worded so the safe answer is the default
 * button. Cancel is the destructive one (leave, lose the edits), which is the
 * opposite of the usual confirm() polarity — so any new call site must read
 * `window.confirm(...) === true` as "user wants to stay", never as "proceed".
 */
export const UNSAVED_CHANGES_MESSAGE = 'Your changes have not been saved.\n\nClick OK to stay on this page, or Cancel to leave and lose them.';

/**
 * Same warning, worded for the Confirm & adjust step.
 *
 * "Not saved" is the wrong word there: each grid edit IS persisted to the
 * built template as you commit it. What is lost by leaving is the BUILD — an
 * unlaunched plan the flow gives you no way back into, so the work has to be
 * redone from step 1.
 */
export const UNLAUNCHED_PLAN_MESSAGE = 'This copy has not been launched yet. If you leave, you will have to start it again.\n\nClick OK to stay on this page, or Cancel to leave.';

/**
 * Same again, for the window where an edit is mid-flight or its save failed.
 * This one is the literal "you will lose this edit" case, so it says so
 * instead of talking about the plan as a whole.
 */
export const UNSAVED_EDIT_MESSAGE = 'Your last edit has not saved yet. If you leave now, you will lose it.\n\nClick OK to stay on this page, or Cancel to leave.';

/**
 * Which of the three the copy flow should show, given where the user is.
 *
 * Pure and exported so the wording rule is pinned by a test rather than living
 * as a ternary in the middle of the container's render.
 *
 * @param {string}  step            the flow's step key ('describe' | 'plan' | 'push')
 * @param {boolean} anyEditUnsaved  a save is in flight, or the last one failed
 */
export function copyLeaveMessage(step, anyEditUnsaved = false) {
  if (step === 'describe') return UNSAVED_CHANGES_MESSAGE;
  // An edit that hasn't landed outranks the plan-level warning: it is the one
  // thing here that is literally about to be lost.
  return anyEditUnsaved ? UNSAVED_EDIT_MESSAGE : UNLAUNCHED_PLAN_MESSAGE;
}

/**
 * Nav surfaces OUTSIDE this workflow's own DOM that we can still intercept.
 *
 * The app mounts a plain <BrowserRouter>, not a data router, so react-router's
 * useBlocker() is unavailable (it throws outside a data router) and there is no
 * generic hook for "some other component called navigate()". The sidebar's nav
 * items are <button onClick={() => navigate(...)}>, so the only thing left to
 * catch them with is a capture-phase click listener on `document`: React binds
 * its own handlers on the root container, which is a DESCENDANT of document, so
 * stopping propagation here runs before — and cancels — the onClick.
 */
const EXTERNAL_NAV_SELECTOR = '.new-sidebar-nav';

/**
 * Warn before leaving a page with edits that were never pushed.
 *
 * Covers the three ways out that a component cannot see on its own:
 *   - reload / tab close / typed URL  → beforeunload (the browser shows its own
 *     generic text here; custom strings have been ignored since ~2017, so the
 *     message below is what the other two surfaces show, not this one)
 *   - browser Back / Forward          → a sentinel history entry + popstate
 *   - the app sidebar's nav buttons   → capture-phase click, cancelled on "stay"
 *
 * In-page exits (breadcrumb, Back/Cancel buttons) go through the returned
 * `confirmLeave()`, which returns true when it is OK to navigate.
 *
 * @param {boolean} isDirty  there are unsaved edits worth warning about
 * @param {string}  message  override for the confirm text
 * @returns {() => boolean}  call before an in-page navigate(); true = go ahead
 */
export default function useUnsavedChangesGuard(isDirty, message = UNSAVED_CHANGES_MESSAGE) {
  // Read inside listeners so the handlers never go stale, and so the effects
  // below depend only on the boolean flipping — not on every render.
  const dirtyRef = useRef(isDirty);
  useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);
  // The message is a REF for the same reason, plus one of its own: the
  // caller's text changes as the flow moves between steps, and re-running the
  // history effect below would push a SECOND sentinel entry every time it did
  // — after which Back needs one extra press per step visited. Listeners read
  // the current text; the effects never re-subscribe for it.
  const messageRef = useRef(message);
  useEffect(() => { messageRef.current = message; }, [message]);
  // Set while we ourselves drive history, so our popstate handler doesn't
  // re-prompt for the navigation the user just approved.
  const leavingRef = useRef(false);

  const confirmLeave = useCallback(() => {
    if (!dirtyRef.current) return true;
    // OK = stay (see UNSAVED_CHANGES_MESSAGE).
    return !window.confirm(messageRef.current);
  }, []);

  // ── reload / tab close / external URL ───────────────────────────────────
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      // Legacy shape some browsers still require to show the prompt at all.
      e.returnValue = messageRef.current;
      return messageRef.current;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // ── browser Back / Forward ──────────────────────────────────────────────
  // popstate fires AFTER the entry is already popped and cannot be cancelled,
  // so arm a duplicate entry for the current URL: the first Back lands right
  // back here, and we either re-arm (stay) or step back once more (leave).
  useEffect(() => {
    if (!isDirty) return undefined;
    window.history.pushState(null, '', window.location.href);
    const onPopState = () => {
      if (leavingRef.current) { leavingRef.current = false; return; }
      if (!dirtyRef.current) return;
      if (window.confirm(messageRef.current)) {
        window.history.pushState(null, '', window.location.href);
        return;
      }
      leavingRef.current = true;
      window.history.back();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isDirty]);

  // ── sidebar nav ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isDirty) return undefined;
    const onCaptureClick = (e) => {
      if (!dirtyRef.current) return;
      const target = e.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest(EXTERNAL_NAV_SELECTOR)) return;
      if (window.confirm(messageRef.current)) {
        // Staying: swallow the click entirely so no navigate() ever runs.
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      }
    };
    document.addEventListener('click', onCaptureClick, true);
    return () => document.removeEventListener('click', onCaptureClick, true);
  }, [isDirty]);

  return confirmLeave;
}
